import { Box, Container, Fade, Stack, styled } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolLink, PoolTypography, useComponentSize, useIsMobile } from '@synergy/components-next';
import { ReactElement, ReactNode, TransitionEvent, useEffect, useRef, useState } from 'react';
import { SortingArrowLeftIcon, SortingArrowRightIcon } from '@synergy/theme-next';
import { To, useNavigate } from 'react-router';
import { shouldForwardPropExclude } from '@synergy/utils';
import { useSwipeable } from 'react-swipeable';

export interface CardSliderProps<T> {
    items: T[];
    title: ReactNode;
    totalItems: number;
    itemWidth?: number;
    showAllUrl?: To;
    renderComponent: (item: T) => ReactElement;
    onLoadMore?: () => void;
}

type SlideAction = 'next' | 'previous' | 'none';

const SlideContainer = styled(Box, {
    shouldForwardProp: shouldForwardPropExclude('slide', 'firstPage', 'scrollStepSize'),
})<{ slide: SlideAction; firstPage: boolean; scrollStepSize: number }>(
    ({ theme, slide, firstPage, scrollStepSize }) => {
        let offset = slide === 'next' ? -scrollStepSize * 2 : slide === 'previous' ? 0 : -scrollStepSize;
        if (firstPage) {
            offset = offset + scrollStepSize;
        }

        return {
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(2),
            transform: `translateX(${offset}px)`,
            transition: slide !== 'none' ? '200ms ease-out' : 'unset',
        };
    }
);

type OverlayPosition = 'left' | 'right';
const OverlayButton = styled(PoolButton, { shouldForwardProp: shouldForwardPropExclude('position') })<{
    position: OverlayPosition;
}>(({ theme, position }) => ({
    position: 'absolute',
    left: position === 'left' ? theme.spacing(3) : undefined,
    right: position === 'right' ? theme.spacing(3) : undefined,
    top: `calc(50% + ${theme.spacing(2)})`,
    transform: `translateY(-50%) translateX(${position === 'left' ? '-50%' : '50%'})`,
    zIndex: theme.zIndex.fab,
}));

export const CardSlider = <T,>(props: CardSliderProps<T>): ReactElement | null => {
    const navigate = useNavigate();
    const { measuredRef, width } = useComponentSize();
    const [page, setPage] = useState(1);
    const [slide, setSlide] = useState<SlideAction>('none');
    const ref = useRef<HTMLDivElement>(null);
    const handleNextPage = (): void => {
        endIndex < props.items.length && setSlide('next');
    };
    const handlePrevPage = (): void => {
        startIndex > 0 && setSlide('previous');
    };
    const swipeHandlers = useSwipeable({ onSwipedLeft: handleNextPage, onSwipedRight: handlePrevPage });
    const isMobile = useIsMobile();

    const cardWidth = (props.itemWidth ?? 287) + 16; // Width (card default) + gap
    const countPerPage = Math.max(1, Math.floor(width / cardWidth)); // Count of cards fitting the container

    // Index of first and last item to visible display on the screen
    const startIndex = (page - 1) * countPerPage;
    const endIndex = startIndex + countPerPage;

    // First item to render including the invisible previous and next page. We also render one more partial card
    const renderStartIndex = Math.max(0, startIndex - countPerPage);
    const renderEndIndex = Math.min(props.items.length, endIndex + countPerPage + 1);
    // Amount to scroll left/right equals the space of the cards, not the container, because we also show a partial card
    const scrollStepSize = countPerPage * cardWidth;

    useEffect(() => {
        // Only if items are already loaded and if there are more.
        if (props.items.length > 0 && props.items.length < Math.min(props.totalItems, endIndex + 10)) {
            props.onLoadMore?.();
        }
        // Only interested in endIndex change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endIndex]);

    const handleSlideEnd = (e: TransitionEvent<HTMLDivElement>): void => {
        // Only listen to container transitions and ignore child transitions
        if (e.target !== ref.current) {
            return;
        }
        setPage(page => (slide === 'next' ? page + 1 : page - 1));
        setSlide('none');
    };

    return (
        <Fade in={props.items.length > 0}>
            <Container maxWidth="xl" style={{ padding: 0 }}>
                <Box sx={{ position: 'relative' }} {...swipeHandlers}>
                    {!isMobile && (
                        <>
                            {startIndex > 0 && (
                                <OverlayButton
                                    position="left"
                                    color="primary"
                                    onClick={handlePrevPage}
                                    variant="containedRound"
                                    startIcon={<SortingArrowLeftIcon />}
                                />
                            )}
                            {endIndex < props.items.length && (
                                <OverlayButton
                                    position="right"
                                    color="primary"
                                    onClick={handleNextPage}
                                    variant="containedRound"
                                    startIcon={<SortingArrowRightIcon />}
                                />
                            )}
                        </>
                    )}

                    <Box sx={{ pb: { xs: 0, sm: 3 }, overflow: 'hidden', px: 1, mx: 2 }}>
                        <Box ref={measuredRef} sx={{ width: '100%', visibility: 'hidden' }} />
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ py: 2 }}>
                            <PoolTypography variant="h5" component="span">
                                {props.title}
                            </PoolTypography>

                            {!isMobile && props.showAllUrl && (
                                <PoolLink colorVariant="primary" href={props.showAllUrl}>
                                    <PoolTypography variant="button">
                                        <FormattedMessage
                                            id="app.slider.showAll"
                                            values={{ value: props.totalItems }}
                                        />
                                    </PoolTypography>
                                </PoolLink>
                            )}
                        </Stack>
                        <SlideContainer
                            ref={ref}
                            slide={slide}
                            onTransitionEnd={handleSlideEnd}
                            firstPage={startIndex <= 0}
                            scrollStepSize={scrollStepSize}
                        >
                            {props.items
                                .slice(renderStartIndex, renderEndIndex)
                                .map(item => props.renderComponent(item))}
                        </SlideContainer>
                        {isMobile && props.showAllUrl && (
                            <PoolButton
                                variant="outlined"
                                color="dark"
                                fullWidth={true}
                                sx={{ mt: 2 }}
                                onClick={() => navigate(props.showAllUrl as string)}
                            >
                                <FormattedMessage id="app.slider.showAll" values={{ value: props.totalItems }} />
                            </PoolButton>
                        )}
                    </Box>
                </Box>
            </Container>
        </Fade>
    );
};
